/**
 * Axios instance configured with session and authentication handling.
 *
 * Features:
 * - Automatically adds X-AR-Session header to all requests
 * - Adds Authorization header with auth token
 * - Handles 401 errors for session/auth expiration
 * - Retries requests after session re-acquisition
 */

import axios from 'axios';
import sessionService from './session';
import authService from './auth';

// Create axios instance
const api = axios.create({
  timeout: 300000, // 5 minutes for long-running operations
});

// Request interceptor - add session and auth headers
api.interceptors.request.use(
  (config) => {
    // Add session header
    const sessionId = sessionService.getSessionId();
    if (sessionId) {
      config.headers['X-AR-Session'] = sessionId;
    }

    // Add auth token
    const authToken = authService.getToken();
    if (authToken) {
      config.headers['Authorization'] = `Bearer ${authToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle session errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle session-related errors
    if (error.response) {
      const { status, data } = error.response;

      // Auth token expired/invalid - redirect to login
      if (status === 401 && data.code === 'AUTH_REQUIRED') {
        console.warn('[API] Authentication required, redirecting to login');
        authService.clearCredentials();
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { message: 'Please login to continue' }
        }));
        return Promise.reject(error);
      }

      // Session expired or invalid
      if (status === 401 && data.code === 'SESSION_EXPIRED' && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Try to re-acquire session
          await sessionService.acquireSession();

          // Retry the original request with new session
          originalRequest.headers['X-AR-Session'] = sessionService.getSessionId();
          return api(originalRequest);
        } catch (sessionError) {
          // Session acquisition failed - redirect to error state
          console.error('[API] Session re-acquisition failed:', sessionError);
          window.dispatchEvent(new CustomEvent('session-error', {
            detail: { message: sessionError.message }
          }));
          return Promise.reject(sessionError);
        }
      }

      // All sessions busy
      if (status === 503) {
        window.dispatchEvent(new CustomEvent('session-error', {
          detail: { message: data.message || 'All sessions are busy. Please try again later.' }
        }));
      }

      // Duplicate file detected
      if (status === 409 && data.code === 'DUPLICATE_FILE') {
        console.warn('[API] Duplicate file detected:', data.message);
        // Let the component handle this
      }
    }

    return Promise.reject(error);
  }
);

export default api;
